// src/components/pages/Pessoa/modules/usePessoaFuncionarioModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';
import * as DataListAPI from '../../../../api/dataList';
import * as TabelaAPI from '../../../../api/tabela';

function createFuncionarioDraft(pessoaId = null) {
    return {
        uuid: null,
        pessoaId: pessoaId || null,
        funcionarioId: null,
        descricao: null,
        ativo: false,
        funcao: 'Adicionar',
    };
}

export function usePessoaFuncionarioModule({ pessoaId, empresaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createFuncionarioDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);
    const [funcionarios, setFuncionarios] = useState({ id: null, descricao: null });
    const [optionsPessoa, setOptionsPessoa] = useState([]);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createFuncionarioDraft(pessoaId));
        setFuncionarios({ id: null, descricao: null });
        setEditingIndex(null);
    }, [pessoaId]);

    const consultarPessoas = useCallback(
        async (filtro) => {
            try {
                const result = await DataListAPI.serviceDataListColaboradorOrRepresentante({
                    empresaId,
                    ativo: true,
                    filtro,
                });
                setOptionsPessoa(result || []);
            } catch (error) {
                console.error(error);
            }
        },
        [empresaId]
    );

    const handleEdit = useCallback(
        (row, index) => {
            const descricao = row?.Pessoa?.descricao || row?.descricao || null;

            setDraft({
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                funcionarioId: row.funcionarioId || null,
                descricao,
                ativo: !!row.ativo,
                funcao: 'Editar',
            });

            setFuncionarios({
                id: row.funcionarioId || null,
                descricao,
            });

            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                funcionarioId: row.funcionarioId || null,
                descricao: row?.Pessoa?.descricao || row?.descricao || null,
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Funcionário removido da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.funcionarios(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir funcionário.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.funcionarioId) {
            setFlashMessage?.('Selecione o funcionário / colaborador.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Funcionário atualizado na lista.', 'success');
            } else {
                let descricao = payload.descricao;

                if (!descricao && payload.funcionarioId) {
                    try {
                        const d = await TabelaAPI.tabelaById(empresaId, 'Pessoa', payload.funcionarioId);
                        descricao = d?.descricao || null;
                    } catch (error) {
                        console.error(error);
                    }
                }

                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        descricao,
                        uuid: payload.uuid || `draft-func-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Funcionário adicionado na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.funcionarios(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar funcionário.', 'error');
        }
    }, [draft, pessoaId, empresaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'funcionarios',
                header: 'Colaboradores vinculados à pessoa',
                columns: [
                    {
                        accessorFn: (row) => row?.Pessoa?.descricao || row?.descricao || '',
                        accessorKey: 'Pessoa.descricao',
                        header: 'Funcionário / Colaborador',
                        size: 240,
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novoFuncionario: draft,
            Funcionarios: funcionarios,
            optionsPessoa: optionsPessoa,
        },
        actions: {
            setNovoFuncionario: setDraft,
            setFuncionarios,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                if (String(funcao).toLowerCase() === 'editar') {
                    handleEdit(row, index);
                    return;
                }
                handleDelete(row, index);
            },
            change: async (e) => {
                const { name, value, checked } = e.target;

                if (name === 'novoFuncionario.descricao') {
                    setFuncionarios((prev) => ({
                        ...prev,
                        descricao: value,
                    }));

                    await consultarPessoas(value);

                    const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        funcionarioId: selectedId ?? null,
                        descricao: value,
                    }));

                    return;
                }

                if (name === 'novoFuncionario.ativo') {
                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        ativo: checked,
                    }));
                }
            },
            focus: () => {},
            blur: () => {},
        },
        columns,
    };
}
